import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import { twMerge } from "tailwind-merge";

/**
 * LivingImage — the site-wide "alive" image treatment (Phase 4).
 *
 * Every non-hero photograph on the site consumes this component. Motion is
 * intentionally slow — users should feel time passing, not notice motion.
 *
 * Five motion layers (`variant="editorial"`):
 *   1. Slow Ken Burns / camera drift  — 90s CSS keyframe, scale 1.00 → 1.04 + translate.
 *   2. Atmospheric light drift        — 35s CSS keyframe, opacity 0.03 → 0.07, soft-light blend.
 *   3. Diagonal reflection sweep      — 60s CSS keyframe, transparent → 6% white → transparent.
 *   4. Layered scroll parallax        — useScroll + useSpring, ±8px (±3px for portrait).
 *   5. Color-temperature oscillation  — 50s CSS keyframe, cool navy ↔ warm gold, capped at 4% opacity.
 *
 * `variant="portrait"` — layers 1 (ultra-slow, scale 1.00 → 1.02 over 120s) + 2 (weaker) + 4 (dampened) only.
 *   Rationale: motion on faces is uncanny. Portraits get "breathing" without pulsing eyes.
 *
 * `variant="still"` — no motion at all. Reserved for images that must not move.
 *
 * Every motion layer collapses to static under `prefers-reduced-motion: reduce`
 * (both via `useReducedMotion()` inside JS AND via a global `@media` rule in
 * index.css — belt-and-braces).
 *
 * Callers may pass `imgStyle` to preserve per-image color grades (existing
 * `saturate()` / `brightness()` filters are not touched by Phase 4). Callers
 * may also pass a decorative `overlayStyle` (used by `ParallaxImage` to keep
 * its contrast-guaranteeing gradient overlays).
 */
export const LivingImage = ({
  src,
  alt,
  className = "",
  imgClassName = "",
  imgStyle,
  variant = "editorial",
  parallax = true,
  eager = false,
  overlayStyle,
  "data-testid": testId,
}) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);

  // Layer 4 — scroll parallax. Range is variant-dependent (portrait is dampened).
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const range = variant === "portrait" ? 3 : 8;
  const rawY = useTransform(scrollYProgress, [0, 1], [range, -range]);
  const springY = useSpring(rawY, {
    stiffness: 100,
    damping: 26,
    mass: 0.6,
    restDelta: 0.5,
  });
  const motionOff = variant === "still" || reduce;
  const yValue = parallax && !motionOff ? springY : 0;

  const kenBurnsClass = motionOff
    ? ""
    : variant === "portrait"
    ? "li-kb-portrait"
    : "li-kb-editorial";

  return (
    <div
      ref={ref}
      className={twMerge("relative overflow-hidden", className)}
      data-testid={testId}
      data-living-image={variant}
    >
      {/* Layer 1 — Ken Burns via CSS keyframe on the wrapping div (which is what
         gets translated by the parallax useSpring). The <img> receives the
         scale+translate keyframe animation directly. */}
      <motion.div
        className={`absolute inset-0 ${kenBurnsClass}`}
        style={{ y: yValue, willChange: motionOff ? "auto" : "transform" }}
      >
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding={eager ? "sync" : "async"}
          fetchpriority={eager ? "high" : "auto"}
          className={`h-full w-full object-cover ${imgClassName}`}
          style={imgStyle}
        />
      </motion.div>

      {/* Layer 2 — atmospheric light drift (all live variants). */}
      {!motionOff && (
        <div
          aria-hidden="true"
          className={
            variant === "portrait" ? "li-drift-portrait" : "li-drift-editorial"
          }
        />
      )}

      {/* Layer 3 — diagonal reflection sweep. Editorial only. */}
      {!motionOff && variant === "editorial" && (
        <div aria-hidden="true" className="li-sweep-wrap">
          <span aria-hidden="true" className="li-sweep" />
        </div>
      )}

      {/* Layer 5 — imperceptible color-temperature oscillation. Editorial only. */}
      {!motionOff && variant === "editorial" && (
        <div aria-hidden="true" className="li-temp" />
      )}

      {/* Decorative caller-provided overlay (contrast gradient etc.). */}
      {overlayStyle ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={overlayStyle}
        />
      ) : null}
    </div>
  );
};

export default LivingImage;
